import { useState, useRef } from 'react'

const SYSTEM_PROMPT = `You are helping a business consultant extract structured data from whiteboard photos taken during strategy sessions.

Analyze the image and extract any of the following that you can identify:

1. ACCOUNTABILITY MAP: Names, roles/functions, and responsibilities. Look for org chart structures, boxes with names, arrows showing hierarchy.

2. SCORECARD METRICS: Any numbers, KPIs, goals, or metrics being tracked. Look for lists of measurements with targets.

3. PRIORITIES / ROCKS: Quarterly goals or priorities. Look for numbered lists, "rocks", OKRs, or "must do" items.

4. ISSUES: Problems, challenges, or items to solve. Look for lists of concerns or blockers.

Respond ONLY with valid JSON in exactly this structure (include only sections you found data for):
{
  "accountabilityMap": {
    "companyName": "string or null",
    "visionary": { "owner": "string", "responsibilities": ["string"] },
    "integrator": { "owner": "string", "responsibilities": ["string"] },
    "departments": [
      { "title": "string", "owner": "string", "responsibilities": ["string"], "children": [] }
    ]
  },
  "scorecard": {
    "metrics": [
      { "name": "string", "owner": "string", "goal": "string", "cadence": "weekly|monthly" }
    ]
  },
  "priorities": {
    "companyRocks": [{ "title": "string", "owner": "string" }],
    "individuals": [{ "name": "string", "rocks": [{ "title": "string" }] }]
  },
  "issues": ["string"],
  "notes": "Any other observations or context from the whiteboard"
}`

export default function SessionImport({ company, onApply }) {
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [applying, setApplying] = useState(false)
  const [applied, setApplied] = useState({})
  const fileRef = useRef(null)

  function handleFile(file) {
    if (!file) return
    setImage(file)
    setResult(null)
    setError(null)
    setApplied({})
    const reader = new FileReader()
    reader.onload = e => setImagePreview(e.target.result)
    reader.readAsDataURL(file)
  }

  async function analyze() {
    if (!image) return
    setAnalyzing(true)
    setError(null)
    setResult(null)

    try {
      const reader = new FileReader()
      const base64 = await new Promise((res, rej) => {
        reader.onload = e => res(e.target.result.split(',')[1])
        reader.onerror = rej
        reader.readAsDataURL(image)
      })

      const mediaType = image.type || 'image/jpeg'

      const response = await fetch('/api/analyze-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base64,
          mediaType,
          companyName: company.name,
          systemPrompt: SYSTEM_PROMPT
        })
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || data.detail || `API error ${response.status}`)
      }
      const text = data.content?.[0]?.text || ''
      if (!text) throw new Error('Empty response from API')
      const clean = text.replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(clean)
      setResult(parsed)
    } catch (e) {
      setError(`Could not analyze the image: ${e.message || 'Unknown error'}. If you uploaded a HEIC file, try converting it to JPG. Otherwise, check that ANTHROPIC_API_KEY is set in Vercel environment variables.`)
      console.error(e)
    }
    setAnalyzing(false)
  }

  async function applySection(section) {
    setApplying(true)
    await onApply(section, result)
    setApplied(prev => ({ ...prev, [section]: true }))
    setApplying(false)
  }

  const hasMap = result?.accountabilityMap && (result.accountabilityMap.visionary?.owner || result.accountabilityMap.departments?.length > 0)
  const hasScorecard = result?.scorecard?.metrics?.length > 0
  const hasPriorities = result?.priorities && (result.priorities.companyRocks?.length > 0 || result.priorities.individuals?.length > 0)
  const hasIssues = result?.issues?.length > 0

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A', marginBottom: 8 }}>Session import</h2>
        <p style={{ fontSize: 13, color: '#888780', lineHeight: 1.6 }}>
          Upload a photo of your whiteboard or session notes. Claude will extract the data and pre-fill the workspace automatically.
        </p>
      </div>

      {/* Upload area */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]) }}
        style={{
          border: `2px dashed ${imagePreview ? '#3B6D11' : '#D0CDC7'}`,
          borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer',
          background: imagePreview ? '#F0FDF4' : '#FAFAF8', marginBottom: 20,
          transition: 'all .2s'
        }}
      >
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
        {imagePreview ? (
          <div>
            <img src={imagePreview} alt="Whiteboard" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 6, marginBottom: 12, objectFit: 'contain' }} />
            <div style={{ fontSize: 12, color: '#3B6D11', fontWeight: 500 }}>✓ {image.name} — click to change</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📸</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#2C2C2A', marginBottom: 6 }}>Drop a photo here or click to upload</div>
            <div style={{ fontSize: 12, color: '#888780' }}>Whiteboard photos, handwritten notes, session diagrams</div>
          </div>
        )}
      </div>

      {imagePreview && !result && (
        <button
          onClick={analyze}
          disabled={analyzing}
          style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 7, background: analyzing ? '#aaa' : '#1E3320', color: '#fff', cursor: analyzing ? 'default' : 'pointer', marginBottom: 20 }}
        >
          {analyzing ? (
            <span>🔍 Analyzing whiteboard…</span>
          ) : (
            <span>✨ Extract data from photo</span>
          )}
        </button>
      )}

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#991B1B' }}>
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#F0FDF4', color: '#166534', fontSize: 11, padding: '3px 10px', borderRadius: 999, border: '1px solid #86EFAC' }}>✓ Analysis complete</span>
            Review what was found and apply to the workspace
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Accountability Map */}
            {hasMap && (
              <ResultCard
                title="Accountability Map"
                icon="🗂"
                applied={applied.map}
                onApply={() => applySection('map')}
                applying={applying}
              >
                {result.accountabilityMap.visionary?.owner && (
                  <ResultRow label="Visionary" value={result.accountabilityMap.visionary.owner} />
                )}
                {result.accountabilityMap.integrator?.owner && (
                  <ResultRow label="Integrator" value={result.accountabilityMap.integrator.owner} />
                )}
                {(result.accountabilityMap.departments || []).map((d, i) => (
                  <ResultRow key={i} label={d.title} value={d.owner} />
                ))}
              </ResultCard>
            )}

            {/* Scorecard */}
            {hasScorecard && (
              <ResultCard
                title="Scorecard metrics"
                icon="📊"
                applied={applied.scorecard}
                onApply={() => applySection('scorecard')}
                applying={applying}
              >
                {result.scorecard.metrics.map((m, i) => (
                  <ResultRow key={i} label={m.name} value={`${m.owner ? m.owner + ' · ' : ''}Goal: ${m.goal || '—'}`} />
                ))}
              </ResultCard>
            )}

            {/* Priorities */}
            {hasPriorities && (
              <ResultCard
                title="90 Day Priorities"
                icon="🎯"
                applied={applied.priorities}
                onApply={() => applySection('priorities')}
                applying={applying}
              >
                {(result.priorities.companyRocks || []).map((r, i) => (
                  <ResultRow key={i} label={r.title} value={r.owner ? `Owner: ${r.owner}` : 'Company rock'} />
                ))}
                {(result.priorities.individuals || []).map((p, i) => (
                  p.rocks.map((r, ri) => (
                    <ResultRow key={`${i}-${ri}`} label={r.title} value={`Owner: ${p.name}`} />
                  ))
                ))}
              </ResultCard>
            )}

            {/* Issues */}
            {hasIssues && (
              <ResultCard
                title="Issues"
                icon="⚠️"
                applied={applied.issues}
                onApply={() => applySection('issues')}
                applying={applying}
              >
                {result.issues.map((issue, i) => (
                  <div key={i} style={{ fontSize: 13, color: '#4A4840', padding: '4px 0', borderBottom: i < result.issues.length - 1 ? '1px solid #F1EFE8' : 'none' }}>
                    {i + 1}. {issue}
                  </div>
                ))}
              </ResultCard>
            )}

            {/* Notes */}
            {result.notes && (
              <div style={{ background: '#FAFAF8', border: '1px solid #EBE7DE', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 8 }}>Claude's notes</div>
                <div style={{ fontSize: 13, color: '#4A4840', lineHeight: 1.7 }}>{result.notes}</div>
              </div>
            )}

            {!hasMap && !hasScorecard && !hasPriorities && !hasIssues && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#888780', fontSize: 13 }}>
                Couldn't extract structured data from this image. Try a clearer photo with better lighting, or make sure the whiteboard text is legible.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ResultCard({ title, icon, children, applied, onApply, applying }) {
  return (
    <div style={{ border: '1px solid #EBE7DE', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: applied ? '#F0FDF4' : '#F9F7F4', borderBottom: '1px solid #EBE7DE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A' }}>{title}</span>
        </div>
        <button
          onClick={onApply}
          disabled={applied || applying}
          style={{
            fontSize: 11, padding: '5px 14px', borderRadius: 5, border: 'none',
            background: applied ? '#22C55E' : '#1E3320',
            color: '#fff', cursor: applied || applying ? 'default' : 'pointer', fontWeight: 500
          }}
        >
          {applied ? '✓ Applied' : applying ? 'Applying…' : 'Apply to workspace'}
        </button>
      </div>
      <div style={{ padding: '12px 16px' }}>{children}</div>
    </div>
  )
}

function ResultRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid #F1EFE8', fontSize: 13 }}>
      <span style={{ fontWeight: 500, color: '#2C2C2A', minWidth: 140 }}>{label}</span>
      <span style={{ color: '#888780' }}>{value}</span>
    </div>
  )
}
